import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/auth.service';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('farmdirectly_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await authService.getMe();
      setUser(data.data.user);
      setStore(data.data.store || null);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('farmdirectly_token');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (emailOrCreds, password) => {
    // Support both login({email, password}) and login(email, password)
    const credentials = typeof emailOrCreds === 'string'
      ? { email: emailOrCreds, password }
      : emailOrCreds;
    const { data } = await authService.login(credentials);

    // Handle MFA challenge response (no token issued yet)
    if (data.mfaRequired) {
      return data; // Return to caller to show MFA form
    }

    localStorage.setItem('farmdirectly_token', data.token);
    setUser(data.user);
    setIsAuthenticated(true);
    // Fetch full user profile (includes store for merchants)
    if (data.user.role === 'farmer') {
      try {
        const meRes = await authService.getMe();
        setStore(meRes.data.data.store || null);
      } catch (e) {
        // Non-critical: store will load on next page load
      }
    }
    return data;
  };

  const register = async (userData) => {
    const { data } = await authService.register(userData);
    localStorage.setItem('farmdirectly_token', data.token);
    setUser(data.user);
    setIsAuthenticated(true);
    // Fetch full user profile (includes store for merchants)
    if (data.user.role === 'farmer') {
      try {
        const meRes = await authService.getMe();
        setStore(meRes.data.data.store || null);
      } catch (e) {
        // Non-critical: store will load on next page load
      }
    }
    return data;
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Continue with local cleanup
    }
    localStorage.removeItem('farmdirectly_token');
    setUser(null);
    setStore(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    setUser,
    store,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    loadUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
